import {
  AmplifyCore,
  appServiceLines,
  commonEnums
} from '@codametrix/ui-common';

const EDITABLE_PATHS: ReadonlySet<string> = new Set(['ipc', 'surgery']);

export const structuralEditPermission = (
  run: CMxAPI.CaseRun | undefined
): 'denied' | 'ok' => {
  if (!run?.caserun_uid) return 'denied';
  if (run.parent_caserun_uid) return 'denied';

  // `isIPC` misses the `'BedsidePro'` literal dev carries: its set holds `'Bedside Pro'` and
  // `normalizeServiceLineName` strips only hyphens. `determineServiceLineConfig` admits it.
  let amplifyPath: string | undefined;
  try {
    amplifyPath = appServiceLines.determineServiceLineConfig({
      name: run.service_line ?? '',
      caseIdentifier: String(run.case_id ?? '')
    })?.amplifyPath;
  } catch (error) {
    // It normalizes the name through `decodeURIComponent`, which throws on a stray `%`. This gate
    // runs unguarded during render, so a throw here would take the whole coding route down. Log
    // first: a silently swallowed throw is indistinguishable from a legitimate deny.
    console.error('Facility edit denied: service line lookup failed', error);
    return 'denied';
  }
  if (!EDITABLE_PATHS.has(amplifyPath ?? '')) return 'denied';

  return 'ok';
};

export const facilityEditPermission = (
  run: CMxAPI.CaseRun | undefined,
  instance: AmplifyAPI.EditGateProcessInstance | null | undefined,
  userContext: CMxAPI.OrganizationContext,
  roles: string[]
): 'allowed' | 'denied' => {
  if (structuralEditPermission(run) === 'denied' || !run || !instance) {
    return 'denied';
  }

  const editable = AmplifyCore.benchEditable.determineBenchEditable(
    instance,
    userContext,
    roles,
    run.parent_caserun_uid ?? null
  ).editable;
  if (!editable) return 'denied';

  if (
    instance.caseType === commonEnums.UIModesEnum.EPIC_EMBEDDED &&
    !appServiceLines.isIPC(run.service_line)
  ) {
    return 'denied';
  }

  return 'allowed';
};
